import React, { useState, useRef, useEffect, useMemo } from 'react';
import Hint from './Hint';
import { useLang } from '../i18n/LangContext';
import BiName from '../i18n/BiName';
import { searchVariants } from '../utils/hangulKeys';   // V154 검색 (영타로 친 한글도)
import { normalizeIngredientName } from '../utils/ingredientMatch';

// V111: 중국 매장(또는 중국어로 보는 사람)이 레시피 탭을 열면 중국어가 비어 있는
// 메뉴명·카테고리·메모를 한 번 자동으로 채운다. 매장(ownerUid)당 세션에 한 번만 —
// 번역이 실패해도 탭을 오갈 때마다 다시 부르지 않게.
const autoFillTried = new Set();

function Sidebar({ categories, selectedId, onSelect, onAddRecipe, onReorder, isMobile, terms = {},
                   ownerUid, onFillAllZh }) {
  const T = {
    recipeItem: '메뉴', ingredient: '식재료',
    lossRateEnabled: false,
    sidebarNewLabel: '새 레시피 추가',
    typeLabels: { semi: '반제품', menu: '메뉴' },
    ...terms,
  };
  const [openCats, setOpenCats] = useState({});
  const { t, lang, country } = useLang();
  const Z = (ko, zh) => (lang === 'zh' ? zh : ko);   // V154

  // ── V154 레시피 검색 — 이름·중국어 이름·코드, 그리고 재료 이름으로도 (예: '양파' → 양파가 들어간 레시피) ──
  const [q, setQ] = useState('');
  const searching = q.trim().length > 0;
  const match = useMemo(() => {
    const qs = [...new Set(searchVariants(q).map(normalizeIngredientName).filter(Boolean))];
    if (!qs.length) return null;
    const hit = (v) => { const k = normalizeIngredientName(v); return !!k && qs.some(x => k.includes(x)); };
    const m = new Map();   // item.id → { by: 'name' | 'ing', ings: [이름] }
    for (const c of categories || []) {
      for (const it of (c && c.items) || []) {
        if (!it) continue;
        if (hit(it.name) || hit(it.name_zh) || hit(it.recipeCode)) { m.set(it.id, { by: 'name', ings: [] }); continue; }
        const ings = (it.ingredients || []).map(i => i && i.name).filter(n => hit(n));
        if (ings.length) m.set(it.id, { by: 'ing', ings });
      }
    }
    return m;
  }, [q, categories]);
  const hitCount = match ? match.size : 0;

  useEffect(() => {
    if (!onFillAllZh || !ownerUid || autoFillTried.has(ownerUid)) return;
    if (country !== 'CN' && lang !== 'zh') return;
    const list = categories || [];
    const missing = list.some(c =>
      (String(c?.name || '').trim() && !c.name_zh) ||
      (c?.items || []).some(i =>
        (String(i?.name || '').trim() && !i.name_zh) ||
        (String(i?.note || '').trim() && !i.note_zh)));
    if (!missing) return;
    autoFillTried.add(ownerUid);
    onFillAllZh().catch(() => {});
  }, [categories, country, lang, ownerUid, onFillAllZh]);

  // 드래그 상태
  const drag = useRef(null);        // { catIdx, itemIdx } 드래그 중인 아이템
  const [dropTarget, setDropTarget] = useState(null); // { catIdx, itemIdx, before: bool }
  const [draggingKey, setDraggingKey] = useState(null); // 드래그 중인 item.id (시각 효과)

  const toggleCat = (catName) => {
    setOpenCats(prev => ({ ...prev, [catName]: !prev[catName] }));
  };

  // ── 드래그 시작
  const handleDragStart = (e, catIdx, itemIdx, itemId) => {
    drag.current = { catIdx, itemIdx };
    setDraggingKey(itemId);
    e.dataTransfer.effectAllowed = 'move';
    // 투명 ghost 이미지 (기본 ghost 숨김)
    const ghost = document.createElement('div');
    ghost.style.position = 'absolute';
    ghost.style.left = '-9999px';
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    setTimeout(() => document.body.removeChild(ghost), 0);
  };

  // ── 드래그 오버 (드롭 위치 계산)
  const handleDragOver = (e, catIdx, itemIdx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!drag.current) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const before = e.clientY < rect.top + rect.height / 2;
    setDropTarget({ catIdx, itemIdx, before });
  };

  // ── 카테고리 끝 영역에 드롭 (카테고리 마지막 항목 뒤)
  const handleDragOverCatEnd = (e, catIdx) => {
    e.preventDefault();
    const cat = categories[catIdx];
    if (!cat) return;
    setDropTarget({ catIdx, itemIdx: cat.items.length, before: true });
  };

  // ── 드롭 실행
  const handleDrop = (e) => {
    e.preventDefault();
    if (!drag.current || !dropTarget || !onReorder) {
      setDropTarget(null);
      setDraggingKey(null);
      drag.current = null;
      return;
    }

    const { catIdx: fromCat, itemIdx: fromItem } = drag.current;
    const { catIdx: toCat, itemIdx: toItem, before } = dropTarget;

    // 깊은 복사
    const next = categories.map(c => ({ ...c, items: [...c.items] }));

    // 꺼내기
    const [moved] = next[fromCat].items.splice(fromItem, 1);

    // 삽입 위치 계산
    let insertAt = toItem;
    if (!before) insertAt = toItem + 1;

    // 같은 카테고리에서 앞쪽으로 이동 시 인덱스 보정
    if (fromCat === toCat && fromItem < insertAt) insertAt -= 1;

    next[toCat].items.splice(Math.max(0, insertAt), 0, moved);

    // 빈 카테고리 제거 안 함 (카테고리는 유지)
    onReorder(next);

    setDropTarget(null);
    setDraggingKey(null);
    drag.current = null;
  };

  const handleDragEnd = () => {
    setDropTarget(null);
    setDraggingKey(null);
    drag.current = null;
  };

  // 드롭 인디케이터 라인이 표시돼야 하는지
  const showIndicatorBefore = (catIdx, itemIdx) =>
    dropTarget &&
    dropTarget.catIdx === catIdx &&
    dropTarget.itemIdx === itemIdx &&
    dropTarget.before;

  const showIndicatorAfter = (catIdx, itemIdx) =>
    dropTarget &&
    dropTarget.catIdx === catIdx &&
    dropTarget.itemIdx === itemIdx &&
    !dropTarget.before;

  const DropLine = () => (
    <div style={{
      height: '2px', background: '#FF6F0F', borderRadius: '2px',
      margin: '0 8px', opacity: 0.9,
      boxShadow: '0 0 4px rgba(30,107,60,0.4)',
    }} />
  );

  return (
    <div style={{
      width: isMobile ? '100%' : '250px',
      background: '#fff',
      borderRight: isMobile ? 'none' : '1px solid #e5e7eb',
      height: '100%',
      overflowY: 'auto',
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
    }}>

      {/* 목록 안내 힌트 */}
      <div style={{ padding: '10px 12px 0' }}>
        <Hint hintKey="sidebar_intro" mb={8}
          zh={T.lossRateEnabled ? undefined : <><strong>售卖菜品</strong>和<strong>半成品</strong>请分开登记。先做好半成品（酱汁·高汤等），就能在多个配方里当食材调用。<strong>拖动配方可以调整顺序。</strong></>}>
          {T.lossRateEnabled
            ? <><strong>시공 항목</strong>과 <strong>부자재</strong>를 등록하세요.
               항목을 <strong>드래그하면 순서를 바꿀 수 있어요.</strong></>
            : <><strong>판매 메뉴</strong>와 <strong>반제품</strong>을 따로 등록하세요.
               반제품(소스·육수 등)을 먼저 만들면 여러 레시피에서 재료로 불러올 수 있어요.
               레시피를 <strong>드래그하면 순서를 바꿀 수 있어요.</strong></>
          }
        </Hint>
      </div>

      {/* 새 항목 추가 버튼 */}
      <div style={{ padding: isMobile ? '0 16px 14px' : '0 12px 12px' }}>
        <button onClick={onAddRecipe}
          style={{
            width: '100%', padding: isMobile ? '14px' : '9px',
            borderRadius: '10px', border: '1.5px dashed #d1d5db',
            background: 'none', color: '#6b7280',
            fontSize: isMobile ? '15px' : '13px',
            cursor: 'pointer', fontWeight: '600',
          }}>
          ＋ {t(T.sidebarNewLabel)}
        </button>
      </div>

      {/* V154 레시피 검색 */}
      <div style={{ padding: isMobile ? '0 16px 12px' : '0 12px 10px' }}>
        <div style={{ position: 'relative' }}>
          <input data-recipe-search value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape') setQ(''); }}
            placeholder={Z('🔍 레시피 찾기 (이름·재료)', '🔍 搜索配方（名称·食材）')}
            style={{ width: '100%', boxSizing: 'border-box', padding: isMobile ? '11px 34px 11px 12px' : '8px 30px 8px 10px',
                     borderRadius: 9, border: `1.5px solid ${searching ? '#FF6F0F' : '#E4E4E7'}`, fontSize: isMobile ? 15 : 13, outline: 'none' }} />
          {searching && (
            <button type="button" data-recipe-search-clear onClick={() => setQ('')} aria-label={Z('지우기', '清除')}
              style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none',
                       color: '#A1A1AA', fontSize: 16, cursor: 'pointer', padding: 4 }}>✕</button>
          )}
        </div>
        {searching && (
          <div data-recipe-search-count style={{ fontSize: 11, color: hitCount ? '#71717A' : '#DC2626', marginTop: 4 }}>
            {hitCount ? Z(`${hitCount}개 찾음`, `找到 ${hitCount} 个`) : Z('찾는 레시피가 없어요', '没有找到配方')}
          </div>
        )}
      </div>

      {/* 카테고리 & 레시피 목록 */}
      <div style={{ flex: 1 }}
        onDragOver={e => e.preventDefault()}
        onDrop={handleDrop}>
        {categories.map((cat, ci) => (searching && !(cat.items || []).some(it => it && match && match.has(it.id))) ? null : (
          <div key={ci}>
            {/* 카테고리 헤더 */}
            <div onClick={() => toggleCat(cat.name)}
              style={{
                padding: isMobile ? '10px 16px' : '8px 14px',
                fontSize: '11px', fontWeight: '700', color: '#9ca3af',
                letterSpacing: '0.05em', textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                userSelect: 'none',
                background: '#f9fafb',
              }}>
              <span style={{ flex: 1 }}>
                {cat.name}
                {cat.name_zh && (
                  <span style={{ display: 'block', marginTop: 2, color: '#1A56A0', fontWeight: 600,
                                 textTransform: 'none', letterSpacing: 0 }}>{cat.name_zh}</span>
                )}
              </span>
              <span style={{ fontSize: 16, color: '#18181B', fontWeight: 700 }}>
                {openCats[cat.name] === false ? '▶' : '▼'}
              </span>
            </div>

            {(searching || openCats[cat.name] !== false) && (
              <>
                {cat.items.map((item, ii) => (searching && !(match && match.has(item.id))) ? null : (
                  <div key={item.id}>
                    {/* 드롭 인디케이터 — 위 */}
                    {showIndicatorBefore(ci, ii) && <DropLine />}

                    {/* 아이템 행 */}
                    <div
                      draggable={!searching}
                      onDragStart={e => handleDragStart(e, ci, ii, item.id)}
                      onDragOver={e => handleDragOver(e, ci, ii)}
                      onDragEnd={handleDragEnd}
                      style={{
                        display: 'flex', alignItems: 'center', width: '100%',
                        opacity: draggingKey === item.id ? 0.4 : 1,
                        transition: 'opacity 0.15s',
                        background: selectedId === item.id ? '#e8f5e9' : 'none',
                        borderLeft: selectedId === item.id ? '4px solid #FF6F0F' : '4px solid transparent',
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'grab',
                        minHeight: isMobile ? '52px' : 'auto',
                      }}>

                      {/* 드래그 핸들 */}
                      <div style={{
                        padding: isMobile ? '0 4px 0 10px' : '0 4px 0 8px',
                        color: '#d1d5db', fontSize: '14px', flexShrink: 0,
                        cursor: 'grab', userSelect: 'none',
                        display: 'flex', alignItems: 'center',
                      }}>
                        ⋮⋮
                      </div>

                      {/* 클릭 영역 */}
                      <button
                        onClick={() => onSelect(item.id)}
                        style={{
                          flex: 1, display: 'flex', alignItems: 'center',
                          textAlign: 'left',
                          padding: isMobile ? '14px 12px 14px 4px' : '9px 10px 9px 4px',
                          border: 'none', background: 'none',
                          cursor: 'pointer',
                          fontSize: isMobile ? '15px' : '13px',
                          color: selectedId === item.id ? '#FF6F0F' : '#374151',
                          fontWeight: selectedId === item.id ? '700' : '400',
                          gap: '8px',
                        }}>
                        <span style={{ flex: 1 }}>
                          <BiName ko={item.name} zh={item.name_zh} size={isMobile ? 12 : 11} />
                          {/* V154: 재료 이름으로 찾았으면 어떤 재료인지 */}
                          {searching && match && match.get(item.id)?.by === 'ing' && (
                            <span data-recipe-search-ing style={{ display: 'block', fontSize: 11, color: '#C2410C', marginTop: 2 }}>
                              {Z('재료', '食材')}: {match.get(item.id).ings.slice(0, 3).join(', ')}
                            </span>
                          )}
                        </span>
                        <span style={{
                          fontSize: '11px', color: item.type === 'semi' ? '#1e40af' : '#52525B',
                          background: item.type === 'semi' ? '#dbeafe' : '#F4F4F5',
                          padding: '3px 8px', borderRadius: '5px', flexShrink: 0,
                        }}>
                          {t(T.typeLabels[item.type] || item.type)}
                        </span>
                        {isMobile && (
                          <span style={{ color: '#d1d5db', fontSize: '16px', marginLeft: '4px' }}>›</span>
                        )}
                      </button>
                    </div>

                    {/* 드롭 인디케이터 — 아래 (마지막 아이템) */}
                    {showIndicatorAfter(ci, ii) && <DropLine />}
                  </div>
                ))}

                {/* 카테고리 빈 드롭존 (항목이 없거나 맨 끝) */}
                <div
                  style={{ height: '8px' }}
                  onDragOver={e => handleDragOverCatEnd(e, ci)}
                />
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Sidebar;
